import { NativeSelect, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useState } from "react";
import { Async, Badge, Empty, PageHead } from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api, type Citations } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useAsync } from "@/lib/hooks";

export function CitationsPage() {
	const t = useT();
	const { locale } = useI18n();
	const [week, setWeek] = useState<string | undefined>(undefined);
	const state = useAsync<Citations>(() => api.citations(week), [week]);

	return (
		<>
			<PageHead title={t("citations.title")} help={t("citations.lede")}>
				{state.data ? (
					<NativeSelect
						aria-label={t("citations.week")}
						value={state.data.week}
						onChange={(e) => setWeek(e.target.value)}
					>
						{[state.data.week, ...state.data.weeks.filter((w) => w !== state.data?.week)].map((w) => (
							<option key={w} value={w}>
								{w}
							</option>
						))}
					</NativeSelect>
				) : null}
			</PageHead>
			<Async state={state}>
				{(c) => (
					<div className="stack">
						{c.gate.length > 0 ? (
							<div className="row">
								<Badge tone={c.engines_passing >= c.engines_needed ? "success" : "warning"}>
									{t("citations.passing", { n: c.engines_passing, total: c.gate.length })}
								</Badge>
								<span className="muted">{t("citations.needed", { n: c.engines_needed })}</span>
							</div>
						) : null}

						{c.gate.length === 0 ? (
							<Empty body={t("citations.noData")} />
						) : (
							<div className="table-scroll">
								<Table className="text-[length:var(--t-sm)]">
									<TableHeader>
										<TableRow>
											<TableHead>{t("citations.engine")}</TableHead>
											<TableHead className="num text-right whitespace-nowrap">
												{t("citations.questionsPassing")}
											</TableHead>
											<TableHead className="num text-right whitespace-nowrap">{t("citations.questions")}</TableHead>
											<TableHead className="num text-right whitespace-nowrap">{t("citations.rate")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{c.gate.map((g) => (
											<TableRow key={g.engine}>
												<TableCell>{g.label}</TableCell>
												<TableCell className="num text-right whitespace-nowrap">{g.passing}</TableCell>
												<TableCell className="num text-right whitespace-nowrap">{g.questions}</TableCell>
												<TableCell className="num text-right whitespace-nowrap">
													<Badge tone={g.rate >= 0.2 ? "success" : "danger"}>{fmt.percent(locale, g.rate)}</Badge>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}

						<div className="grid-2">
							<div className="data-section">
								<h2>{t("citations.raw")}</h2>
								{c.raw.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table className="text-[length:var(--t-sm)]">
										<TableHeader>
											<TableRow>
												<TableHead>{t("citations.engine")}</TableHead>
												<TableHead className="num text-right whitespace-nowrap">{t("citations.cited")}</TableHead>
												<TableHead className="num text-right whitespace-nowrap">{t("citations.questions")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{c.raw.map((r) => (
												<TableRow key={r.engine}>
													<TableCell>{r.label}</TableCell>
													<TableCell className="num text-right whitespace-nowrap">{r.cited}</TableCell>
													<TableCell className="num text-right whitespace-nowrap">{r.questions}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>
							<div className="data-section">
								<h2>{t("citations.byKind")}</h2>
								{c.by_kind.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table className="text-[length:var(--t-sm)]">
										<TableHeader>
											<TableRow>
												<TableHead>{t("citations.kind")}</TableHead>
												<TableHead className="num text-right whitespace-nowrap">{t("citations.cited")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{c.by_kind.map((k) => (
												<TableRow key={k.kind}>
													<TableCell>{k.kind}</TableCell>
													<TableCell className="num text-right whitespace-nowrap">{k.count}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>
						</div>
					</div>
				)}
			</Async>
		</>
	);
}
